export const AUTH_EVENTS = Object.freeze({
  AUTH_ENTRY_CLICKED: {
    name: 'auth_entry_clicked',
    category: 'Authentication',
    description: '用户点击登录入口时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'redirect_target', 'redirect_present', 'reason_code', 'lang'
    ]
  },
  AUTH_PAGE_VIEWED: {
    name: 'auth_page_viewed',
    category: 'Authentication',
    description: '登录、注册或邮箱确认提示页面/状态曝光时触发',
    // 页面曝光类事件显式抬到 full 档（2026-10-27）：与 landing_page_viewed 同口径，
    // 让「一进站就跳登录页、之后流失」的用户也有会话级 timezone / browser_language 锚点。
    // 预算：声明峰值 11 + 5 + 1 = 17 ≪ 25，安全。
    globalsProfile: 'full',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'auth_flow', 'method',
      'redirect_target', 'redirect_present', 'is_logged_in', 'reason_code', 'lang',
      'navigation_trigger'
    ]
  },
  AUTH_MODE_SWITCHED: {
    name: 'auth_mode_switched',
    category: 'Authentication',
    description: '用户在登录、注册和返回登录之间切换时触发',
    requiredParams: ['auth_flow'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'reason_code'
    ]
  },
  AUTH_SUBMIT_STARTED: {
    name: 'auth_submit_started',
    category: 'Authentication',
    description: '邮箱登录或注册表单通过本地校验并开始提交时触发',
    requiredParams: ['auth_flow', 'method'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'redirect_target', 'redirect_present', 'email_domain', 'is_business_email'
    ]
  },
  AUTH_SUBMIT_COMPLETED: {
    name: 'auth_submit_completed',
    category: 'Authentication',
    description: '邮箱登录或注册提交成功、失败、阻断或需要邮箱确认时触发',
    requiredParams: ['auth_flow', 'method', 'action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'reason_code', 'error_code',
      'error_message', 'http_status', 'duration_ms', 'redirect_target', 'redirect_present',
      'email_domain', 'is_business_email'
    ]
  },
  AUTH_OAUTH_STARTED: {
    name: 'auth_oauth_started',
    category: 'Authentication',
    description: '有两个语义不同的来源，判读必须看 element_name / method，勿只看事件名：① 用户在 Login 页点击 OAuth 按钮（element_name=<provider>_oauth_button、method=google|twitter|discord、auth_flow=oauth_login，带 redirect_target/redirect_present）——代表真实的登录意图；② Login/Landing 页挂载时自动初始化 Google One Tap 浮层（element_name=google_one_tap_prompt、method=google_one_tap、auth_flow=one_tap_login）——只是初始化，不代表用户有任何登录意图（浮层是否真的弹出/被跳过看 auth_one_tap_prompt_completed）',
    requiredParams: ['method'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'auth_flow', 'redirect_target', 'redirect_present'
    ]
  },
  AUTH_OAUTH_REDIRECT_STARTED: {
    name: 'auth_oauth_redirect_started',
    category: 'Authentication',
    description: 'OAuth provider 跳转发起成功或失败时触发',
    requiredParams: ['method', 'action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'auth_flow',
      'redirect_target', 'redirect_present', 'reason_code', 'error_code', 'duration_ms'
    ]
  },
  AUTH_OAUTH_CALLBACK_COMPLETED: {
    name: 'auth_oauth_callback_completed',
    category: 'Authentication',
    description: 'OAuth callback 处理 token、setSession 和用户态写入完成或失败时触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'auth_flow', 'method',
      'session_source', 'reason_code', 'error_code', 'error_message', 'duration_ms',
      'redirect_target', 'redirect_present'
    ]
  },
  AUTH_EMAIL_CONFIRMATION_PROMPT_VIEWED: {
    name: 'auth_email_confirmation_prompt_viewed',
    category: 'Authentication',
    description: '邮箱确认提示曝光时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'auth_flow', 'method',
      'reason_code', 'email_domain', 'is_business_email'
    ]
  },
  AUTH_CONFIRMATION_EMAIL_RESEND_STARTED: {
    name: 'auth_confirmation_email_resend_started',
    category: 'Authentication',
    description: '用户点击重发确认邮件并开始请求时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'auth_flow', 'method', 'email_domain', 'is_business_email'
    ]
  },
  AUTH_CONFIRMATION_EMAIL_RESEND_COMPLETED: {
    name: 'auth_confirmation_email_resend_completed',
    category: 'Authentication',
    description: '重发确认邮件成功、失败或被阻断时触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'auth_flow', 'method',
      'reason_code', 'error_code', 'error_message', 'http_status', 'duration_ms',
      'email_domain', 'is_business_email'
    ]
  },
  AUTH_REDIRECT_COMPLETED: {
    name: 'auth_redirect_completed',
    category: 'Authentication',
    description: '登录或 OAuth callback 后业务跳转完成、失败或跳过时触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'auth_flow', 'method',
      'redirect_target', 'redirect_present', 'reason_code', 'duration_ms'
    ]
  },
  AUTH_SESSION_CHECK_COMPLETED: {
    name: 'auth_session_check_completed',
    category: 'Authentication',
    description: '登录态检查、会话恢复或会话缺失判定完成时触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'session_source', 'is_logged_in',
      'reason_code', 'error_code', 'duration_ms'
    ]
  },
  AUTH_LOGOUT_STARTED: {
    name: 'auth_logout_started',
    category: 'Authentication',
    description: '用户开始退出登录时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'auth_flow'
    ]
  },
  AUTH_LOGOUT_COMPLETED: {
    name: 'auth_logout_completed',
    category: 'Authentication',
    description: '退出登录成功或失败时触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'auth_flow', 'reason_code',
      'error_code', 'error_message', 'duration_ms', 'redirect_target'
    ]
  },
  AUTH_USER_MENU_TOGGLED: {
    name: 'auth_user_menu_toggled',
    category: 'Authentication',
    description: '已登录用户头像菜单打开或关闭时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'menu_state', 'reason_code'
    ]
  },
  AUTH_ONE_TAP_PROMPT_COMPLETED: {
    name: 'auth_one_tap_prompt_completed',
    category: 'Authentication',
    description: 'Google One Tap prompt 展示、跳过、关闭或不可展示状态回调时触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'auth_flow', 'method',
      'reason_code', 'error_code', 'redirect_target', 'redirect_present', 'duration_ms'
    ]
  },
  AUTH_ONE_TAP_SELECTED: {
    name: 'auth_one_tap_selected',
    category: 'Authentication',
    description: '用户点击/选凭证通过 Google One Tap 浮层选中凭据的那一刻触发（仅表示选凭据动作，不含成功/失败；成功/失败由 auth_oauth_callback_completed 承载）',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'auth_flow', 'method',
      'redirect_target', 'redirect_present'
    ]
  }
});
